import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { useTranslation } from '../i18n';
import { PageHeader, Section } from '../components/Section';
import { RecentGenerationsStrip } from '../components/RecentGenerationsStrip';
import { JobResult } from '../components/JobResult';
import { getFingerprintNow } from '../hooks/useFingerprint';
import { useSignupNudge } from '../components/SignupNudge';
import { usePriceQuote } from '../hooks/usePriceQuote';

type Model = 'qwen-image-edit' | 'nano-banana-edit' | 'nano-banana-2-edit';
type UploadResult = { url: string; r2_key: string };

const ASPECTS = ['auto', '16:9', '4:3', '1:1', '3:4', '9:16'] as const;

async function uploadImage(file: File): Promise<UploadResult> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/api/v1/image-edit/upload', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`upload failed: ${res.status} ${body}`);
  }
  return (await res.json()) as UploadResult;
}

function ImageInput({
  label,
  value,
  onChange,
  onClear,
}: {
  label: string;
  value: UploadResult | null;
  onChange: (v: UploadResult) => void;
  onClear: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await uploadImage(f));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
      {value ? (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, bgcolor: 'action.hover' }}
        >
          <Box
            component="img"
            src={value.url}
            alt={label}
            sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}
          />
          <Typography variant="body2" sx={{ flex: 1, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }} noWrap>
            {value.r2_key.split('/').slice(-1)[0]}
          </Typography>
          <IconButton size="small" onClick={onClear}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ) : (
        <Button
          component="label"
          variant="outlined"
          fullWidth
          startIcon={busy ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          sx={{ justifyContent: 'flex-start', py: 2.25, borderStyle: 'dashed', color: 'text.secondary' }}
          disabled={busy}
        >
          {busy ? 'Uploading…' : 'Choose image'}
          <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={handle} />
        </Button>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

export default function ImageEdit() {
  const t = useTranslation();
  const location = useLocation();
  const remix = (location.state as { remix?: Record<string, unknown> } | null)?.remix ?? null;

  const [model, setModel] = useState<Model>((remix?.model as Model | undefined) ?? 'qwen-image-edit');
  const [img1, setImg1] = useState<UploadResult | null>(null);
  const [img2, setImg2] = useState<UploadResult | null>(null);
  const [img3, setImg3] = useState<UploadResult | null>(null);
  const [prompt, setPrompt] = useState((remix?.prompt as string | undefined) ?? '');
  const [aspect, setAspect] = useState<(typeof ASPECTS)[number]>((remix?.aspect_ratio as (typeof ASPECTS)[number] | undefined) ?? '1:1');
  const [minHeight, setMinHeight] = useState<480 | 720 | 1080>((remix?.min_height as 480 | 720 | 1080 | undefined) ?? 720);
  const [submitting, setSubmitting] = useState(false);
  const [activityId, setActivityId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = prompt.trim().length > 0 && !!img1 && !submitting;
  const credits = usePriceQuote({ endpoint: 'image-edit', model, minHeight });

  const { gateGenerate } = useSignupNudge();

  const handleSubmit = async () => {
    // Anonymous users get the signup nudge instead of a doomed 402.
    if (gateGenerate()) return;
    if (!img1) return;
    setSubmitting(true);
    setError(null);
    try {
      const fingerprint = await getFingerprintNow();
      const res = await fetch('/api/v1/image-edit/generate-async', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          model,
          aspect_ratio: aspect,
          min_height: minHeight,
          image_url: img1.url,
          image_r2_key: img1.r2_key,
          second_image_url: img2?.url,
          second_image_r2_key: img2?.r2_key,
          third_image_url: img3?.url,
          third_image_r2_key: img3?.r2_key,
          fingerprint,
        }),
      });
      if (res.status === 402) {
        const body = await res.json();
        throw new Error(`Insufficient credits (need ${body.required}, have ${body.available})`);
      }
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`submit failed: ${res.status} ${body}`);
      }
      const data = await res.json();
      setActivityId(data.activity_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setActivityId(null);
    setImg1(null);
    setImg2(null);
    setImg3(null);
    setPrompt('');
  };

  return (
    <Box>
      <PageHeader title={t.imageEdit.title} description={t.imageEdit.description} />
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>

      {activityId ? (
        <Section title={t.imageEdit.resultTitle}>
          <Paper sx={{ p: 3 }}>
            <JobResult
              activityId={activityId}
              resultType="image"
              onReset={reset}
              resetLabel={t.imageEdit.newGeneration}
            />
          </Paper>
        </Section>
      ) : (
        <>
          <Section title={t.imageEdit.modelTitle}>
            <Paper sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <TextField
                  select
                  label={t.imageEdit.model}
                  value={model}
                  onChange={(e) => setModel(e.target.value as Model)}
                  size="small"
                  sx={{ maxWidth: 320 }}
                >
                  <MenuItem value="qwen-image-edit">Qwen Image Edit</MenuItem>
                  <MenuItem value="nano-banana-edit">Nano Banana Edit</MenuItem>
                  <MenuItem value="nano-banana-2-edit">Nano Banana 2 Edit</MenuItem>
                </TextField>
                <TextField
                  select
                  label={t.imageEdit.aspectRatio}
                  value={aspect}
                  onChange={(e) => setAspect(e.target.value as (typeof ASPECTS)[number])}
                  size="small"
                  sx={{ maxWidth: 220 }}
                >
                  {ASPECTS.map((a) => (
                    <MenuItem key={a} value={a}>{a}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label={t.imageEdit.resolution}
                  value={minHeight}
                  onChange={(e) => setMinHeight(Number(e.target.value) as 480 | 720 | 1080)}
                  size="small"
                  sx={{ maxWidth: 220 }}
                >
                  <MenuItem value={480}>480p</MenuItem>
                  <MenuItem value={720}>720p</MenuItem>
                  <MenuItem value={1080}>1080p</MenuItem>
                </TextField>
              </Stack>
            </Paper>
          </Section>

          <Section
            title={t.imageEdit.imagesTitle}
            description={t.imageEdit.imagesDescription}
          >
            <Paper sx={{ p: 3 }}>
              <Stack spacing={3}>
                <ImageInput label={t.imageEdit.image1} value={img1} onChange={setImg1} onClear={() => setImg1(null)} />
                <ImageInput label={t.imageEdit.image2} value={img2} onChange={setImg2} onClear={() => setImg2(null)} />
                <ImageInput label={t.imageEdit.image3} value={img3} onChange={setImg3} onClear={() => setImg3(null)} />
              </Stack>
            </Paper>
          </Section>

          <Section title={t.imageEdit.prompt}>
            <Paper sx={{ p: 3 }}>
              <TextField
                fullWidth
                placeholder={t.imageEdit.promptPlaceholder}
                multiline
                minRows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </Paper>
          </Section>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" size="large" disabled={!canSubmit} onClick={handleSubmit}>
              {submitting ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                `${t.imageEdit.generate}${credits != null ? ` (${credits} ${t.credits.balanceUnit})` : ''}`
              )}
            </Button>
          </Stack>
        </>
      )}
      </Box>
      <RecentGenerationsStrip endpoint="image-edit" />
      </Box>
    </Box>
  );
}
